import { IIconProps } from "@fluentui/react";
import { useEffect } from "react";
import { Protocol } from "../../../models/Entities/Protocols/Protocol";
import { ErrorType } from "../../../models/HttpError";

// stores
import * as SettlementStore from '../../../redux/middleware/SettlementMiddleware';
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';
import * as MedicalProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';
import * as SponsorStore from '../../../redux/middleware/SponsorMiddleware';
import * as PractitionerStore from '../../../redux/middleware/PractitionerMiddleware';
import * as MedicalInsuranceStore from '../../../redux/middleware/MedicalInsuranceMiddleware';

//State
import { AppState } from "../../../redux/reducers";
import { Settlement } from "../../../models/Entities/Settlements/Settlement";
import { Sponsor } from "../../../models/Entities/Sponsor/Sponsor";
import { Practitioner } from "../../../models/Entities/Practitioners/Practitioner";
import { MedicalInsurance } from "../../../models/Entities/MedicalInsurances/MedicalInsurance";

interface IProps {
    SettlementId: number;
}
  
  interface ConnectedProps {
    isLoading: boolean;
    isUpdating: boolean;
    isUpdatedSuccessfully: boolean;
    isLoadingSuccessfully: boolean;
    failOnUpdating: boolean;
    failOnLoading: boolean;
    isChangingStatus: boolean;
    changeStatusSuccessfully: boolean;
    failOnChangeStatus: boolean;
    error: ErrorType | undefined;
    Settlement: Settlement | undefined;
    settlementEntityLabel: string | undefined;
    settlementEntityType: 'sponsor' | 'practitioner' | 'agreement' | undefined;
        accessToken: string | undefined;
  }
  
  interface DispatchProps {
    GetSettlementById: typeof SettlementStore.actionCreators.GetSettlementById;
    ChangeSettlementStatus: typeof SettlementStore.actionCreators.ChangeSettlementStatus;
    ResetSettlementChangeStatusFlags: typeof SettlementStore.actionCreators.ResetSettlementChangeStatusFlags;
    GetMedicalProtocolById: typeof MedicalProtocolStore.actionCreators.GetMedicalProtocolById;
    GetContractById: typeof ContractStore.actionCreators.GetContractById;
    GetSponsorById: typeof SponsorStore.actionCreators.GetSponsorById;
    GetPractitionerById: typeof PractitionerStore.actionCreators.GetPractitionerById;
    GetMedicalInsuranceById: typeof MedicalInsuranceStore.actionCreators.GetMedicalInsuranceById;
  }
  
  export type Props = IProps & ConnectedProps & DispatchProps;
  
  export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const currentSettlement: Settlement | undefined = state.Settlement?.list?.find((item: Settlement) => item.id === ownProps.SettlementId );

    return {
            accessToken: state.oidc?.user?.access_token,
            isLoading: state.Settlement?.isLoadingAll,
            isUpdating: state.Settlement?.isLoadingAll,
            isUpdatedSuccessfully: state.Settlement?.successLoadingAll,
            isLoadingSuccessfully: state.Settlement?.successLoadingAll,
            failOnUpdating: state.Settlement?.failOnLoadingAll,
            failOnLoading: state.Settlement?.failOnLoadingAll,
            isDeleting: state.Settlement?.isDeletingOne,
            isDeletedSuccessfully: state.Settlement?.successDeletingOne,
            failOnDeleting: state.Settlement?.failOnDeletingOne,
            isChangingStatus: currentSettlement?.isChangingStatus,
            changeStatusSuccessfully: currentSettlement?.successChangingStatus,
            failOnChangeStatus: currentSettlement?.failOnChangeStatus,
            error: currentSettlement?.error,
            Settlement: currentSettlement,
            ...(() => {
                if (!currentSettlement) return { settlementEntityLabel: undefined, settlementEntityType: undefined };
                if ((currentSettlement.sponsor?.filter(Boolean)?.length ?? 0) > 0) {
                    const sponsor: Sponsor | undefined = state.Sponsor?.list?.find((s: Sponsor) => s.id === currentSettlement.sponsor[0]?.sponsorId);
                    const name = sponsor
                        ? (sponsor.person?.businessName || [sponsor.person?.firstName, sponsor.person?.lastName].filter(Boolean).join(' '))
                        : undefined;
                    return { settlementEntityLabel: name, settlementEntityType: 'sponsor' as const };
                }
                if ((currentSettlement.practitioner?.length ?? 0) > 0 && (currentSettlement.protocol?.length ?? 0) > 0) {
                    const prac: Practitioner | undefined = state.Practitioners?.list?.find((p: Practitioner) => p.userId === currentSettlement.practitionerId);
                    return { settlementEntityLabel: prac?.fullName, settlementEntityType: 'practitioner' as const };
                }
                if ((currentSettlement.agreements?.length ?? 0) > 0) {
                    const mi: MedicalInsurance | undefined = state.MedicalInsurances?.list?.find((m: MedicalInsurance) => m.id === currentSettlement.medicalInsuranceId);
                    return { settlementEntityLabel: mi?.businessName || mi?.title, settlementEntityType: 'agreement' as const };
                }
                return { settlementEntityLabel: undefined, settlementEntityType: undefined };
            })()
        }
    };
  
  export const mapDispatchToProps = {
    ...SettlementStore.actionCreators,
    ...MedicalProtocolStore.actionCreators,
    ...ContractStore.actionCreators,
    GetSponsorById: SponsorStore.actionCreators.GetSponsorById,
    GetPractitionerById: PractitionerStore.actionCreators.GetPractitionerById,
    GetMedicalInsuranceById: MedicalInsuranceStore.actionCreators.GetMedicalInsuranceById,
  };

    // Calls GetSettlementById when changeStatusSuccessfully flips to true
    export const useSettlementStatusRefresh = (
        settlementId: number | undefined,
        changeStatusSuccessfully: boolean | undefined,
        GetSettlementById: DispatchProps["GetSettlementById"]
    ) => {
        useEffect(() => {
            if (settlementId && changeStatusSuccessfully) {
                GetSettlementById(settlementId);
            }
        }, [settlementId, changeStatusSuccessfully, GetSettlementById]);
    };

  /*
    Contract Status
    _____________________
    Active = 1,
    Inactive = 2,
    Suspended = 3,
    Expired = 4,
    Canceled = 5,
    Terminated = 6
*/

export const GetSettlementStatusDescripcion = (status: number | undefined) => {
    switch (status) {
      case undefined:
          return 'SettlementStatus.pending'; // "Pendiente";
        case 1:
            return 'SettlementStatus.registered'; // "Registrado";
        case 2:
            return 'SettlementStatus.inProcess'; // "En proceso";
        case 3:
            return 'SettlementStatus.finished'; // "Finalizado";
        case 4:
            return 'SettlementStatus.cancelled'; // "Cancelado";
        case 5:
            return 'SettlementStatus.Confirmed'; // "Conformado";
        case 6:	
            return 'SettlementStatus.rejected'; // "Rechazado";
        case 7:
            return 'SettlementStatus.fail'; // "Fallido";
        case 8:
            return 'SettlementStatus.paid'; // "Pagado";
        case 9:
            return 'SettlementStatus.debtUnpaid'; // "Deuda impaga";
        default:
        return 'SettlementStatus.pending'; // "Pendiente";
    }
  };

  export const GetContractStatusClass = (status: number | undefined) => {
    switch (status) {
        case undefined:
            return "";
        case 1:
            return "Active";
        case 2:
            return "Inactive";
        case 3:
            return "Suspended";
        case 4:
            return "Expired";
        case 5:
            return "Canceled";
        case 6:
            return "Terminated";
        default:
        return "";
  }
};

export const GetSettlementStatusIcon = (status: number | undefined) => {
    switch (status) {
        case undefined:
            return 'WaitlistConfirmMirrored';
        case 1:
            return 'WaitlistConfirmMirrored';
        case 2:
            return 'SyncStatus';
        case 3:
            return 'SkypeCircleCheck';
        case 4:
            return 'StatusErrorFull';
        case 5:
            return 'Completed';    // Confirmado
        case 6:	
            return 'StatusErrorFull';
        case 7:
            return 'ErrorBadge';
        case 8:
            return 'PaymentCard';  // Pagado
        case 9:
            return 'Warning';
        default:
        return 'WaitlistConfirmMirrored';
    }
  }


  //#region Icons
export const PropertiesIcon: IIconProps = { iconName: 'DietPlanNotebook' };
export const AddIcon: IIconProps = { iconName: 'CirclePlus' };
export const ViewIcon: IIconProps = { iconName: 'View' };
export const LinkContractIcon: IIconProps = { iconName: 'Link' };